import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import debounce from "lodash.debounce";
import { format } from "date-fns";
import GenericTableComponent from "../common/Table";
import { getTopCampaigns } from "../../redux/slices/reportSlice";
import { useNavigate } from "react-router-dom";

import Swal from "sweetalert2";

const columns = [
  { id: "campaignName", label: "Campaign name" },
  { id: "createdAt", label: "Date created" },
  { id: "eCPM", label: "eCPM" },
  { id: "conversionRate", label: "CR" },
  { id: "reach", label: "Clicks" },
  { id: "costPerUser", label: "CPA" },
  { id: "acquistion", label: "Conversions" },
  { id: "amountSpent", label: "Amount" },
  { id: "hoardedConversion", label: "Hoarded conversions" },
  { id: "totalHoardedConversionAmount", label: "Hoarded amount" },
  { id: "totalConversion", label: "Total conversions" },
  //{ id: "hoardingPaid", label: "Paid" },
];

const CampaignTable = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { topCampaigns, loading, meta } = useSelector((state) => state.report);
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [dateRange, setDateRange] = useState(() => {
    const now = new Date();
    return {
      startDate: new Date(now.getFullYear(), now.getMonth(), 1),
      endDate: now,
    };
  });
  const [exportData] = useState(true)
  const [exportLoading, setExportLoading] = useState(false);

  useEffect(() => {
    const params = { page, rowsPerPage };

    if (dateRange.startDate && dateRange.endDate) {
      params.startDate = format(dateRange.startDate, "yyyy-MM-dd");
      params.endDate = format(dateRange.endDate, "yyyy-MM-dd");
    }

    if (searchFilter) {
      params.search = searchFilter;
    }

    dispatch(getTopCampaigns(params));
  }, [dateRange, dispatch, page, rowsPerPage, searchFilter]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  
  const debouncedSearch = useMemo(
    () =>
      debounce((searchTerm) => {
        setSearchFilter(searchTerm);
        setPage(0);
      }, 2000),
    [setSearchFilter, setPage]
  );

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearch(value);
    debouncedSearch(value);
  };
  
  const triggerSearch = () => {
    setSearchFilter(search);
    setPage(0);
  };
  
  const handleExport = async () => {
    try {
      setExportLoading(true);
      
      // Get current date and 7 days ago
      const endDate = dateRange.endDate 
        ? format(dateRange.endDate, "yyyy-MM-dd")
        : format(new Date(), "yyyy-MM-dd");
      
      const startDate = dateRange.startDate 
        ? format(dateRange.startDate, "yyyy-MM-dd")
        : format(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd");
      
      const response = await 
      dispatch(getTopCampaigns({startDate, endDate, exportData})).unwrap();

      // Get the file URL from the response
      if (response.data.file) {
        // Create a temporary link and trigger download
        const link = document.createElement('a');
        link.href = response.data.file;
        link.setAttribute('download', `Campaign-report.csv`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      } else {
        Swal.fire({
          title: 'Export Status',
          text: response.data.message || 'No file available for download',
          icon: 'info',
          confirmButtonColor: '#03a9f4'
        });
      }
    } catch (error) {
      console.error('Export failed:', error);
      Swal.fire({
        title: 'Export Failed',
        text: error.message || 'Failed to export publisher details',
        icon: 'error',
        confirmButtonColor: '#03a9f4'
      });
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div style={{ padding: "0" }}>
      {loading ? (
        <div> </div>
      ) : (
        // ) : topCampaigns.length === 0 ? (
        //   <div style={{ textAlign: "center", marginTop: "40px" }}>
        //     No campaigns available.
        //   </div>
        <GenericTableComponent
          columns={columns}
          data={topCampaigns}
          filterSearch={true}
          paginate={true}
          page={page}
          rowsPerPage={rowsPerPage}
          handlePageChange={handlePageChange}
          handleRowsPerPageChange={handleRowsPerPageChange}
          setDateRange={setDateRange}
          dateRange={dateRange}
          totalItems={meta?.itemCount || 0}
          handleSearchChange={handleSearchChange}
          triggerSearch={triggerSearch}
          search={search}
          hideActiveSearch
          reportView
          detailLink="/campaign-details"
          detailLinkText="View details"
          viewLink="/campaign-publishers"
          viewLinkText="View publishers"
          showExport={true}
          onExport={handleExport}
          exportLoading={exportLoading}
          onRowClick={(row) => {
            const startDate = format(dateRange.startDate || new Date(Date.now() - 7 * 24 * 60 * 60 * 1000), "yyyy-MM-dd");
            const endDate = format(dateRange.endDate || new Date(), "yyyy-MM-dd");
            navigate(`/campaign-details/${row.campaignReference}?startDate=${startDate}&endDate=${endDate}&name=${encodeURIComponent(row.campaignName)}`,
          );
          }}
        />
      )}
    </div>
  );
};

export default CampaignTable;
